import { useCallback, useEffect, useRef } from "react";
import { flush, track } from "../lib/usageTracker";

/**
 * Usage counts for the log-a-match flow (#1110, D218). Watches the page's own
 * step state and says which step was reached, left or finished. Reads no form
 * field, no ground and no match: only the step word.
 *
 * Counts the redrawn flow only (`redrawn`: the page passes `newUi("log-ground")`):
 * the shipped dialog has no "step 1", so its numbers would not mean the same thing. An EDIT is not part
 * of the flow and is never counted.
 *
 *   - `log.start`        the flow opens on step 1
 *   - `log.add_ground`   step 1 moves to its add-a-ground panel
 *   - `log.step_done`    a forward step: ground (step 1 to 2) or scorecard (the save)
 *   - `log.saved`        the match was saved (`markSaved`, called on success)
 *   - `log.left_unsaved` the flow closed, or the page was hidden, before saving
 *                        (item: the step the person was on). Once per flow.
 *
 * `log.near_me` and `log.search` are counted where those controls live.
 *
 * Returns `markSaved`, which the page calls once the save request succeeded.
 */
const itemOfStep = (step) => (step === "form" ? "scorecard" : step === "ground" || step === "add-ground" ? "ground" : null);

export function useLogFlowUsage({ redrawn, open, editing, logStep }) {
  const live = Boolean(redrawn && open && !editing);
  const liveRef = useRef(false);
  const stepRef = useRef(null);
  const savedRef = useRef(false);
  const leftRef = useRef(false);

  useEffect(() => {
    liveRef.current = live;
    const prev = stepRef.current;
    if (!live) {
      if (prev !== null && !savedRef.current && !leftRef.current && itemOfStep(prev)) {
        track("log.left_unsaved", { item: itemOfStep(prev) });
      }
      stepRef.current = null;
      savedRef.current = false;
      leftRef.current = false;
      return;
    }
    if (prev === null) {
      // A refresh that restores a later step resumes a flow already started.
      if (logStep === "ground") track("log.start");
    } else if (prev !== logStep) {
      if (logStep === "add-ground") track("log.add_ground");
      if (logStep === "form" && (prev === "ground" || prev === "add-ground")) {
        track("log.step_done", { item: "ground" });
      }
    }
    stepRef.current = logStep;
  }, [live, logStep]);

  // Hiding the page before saving counts as leaving, once, and is sent at
  // once because a hidden page may not get another chance.
  useEffect(() => {
    if (!live) return undefined;
    const leave = () => {
      const item = itemOfStep(stepRef.current);
      if (savedRef.current || leftRef.current || !item) return;
      leftRef.current = true;
      track("log.left_unsaved", { item });
      flush();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") leave();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
    };
  }, [live]);

  return useCallback(() => {
    if (!liveRef.current || savedRef.current) return;
    savedRef.current = true;
    track("log.step_done", { item: "scorecard" });
    track("log.saved");
  }, []);
}

export default useLogFlowUsage;
